/**
 * The scope one inspection runs under: a property, one exact URL inside it, and the language
 * Google's issue messages come back in.
 *
 * There is no window, no surface and no comparison — URL Inspection answers about the indexed
 * version of one URL as Google holds it now — so none of the shared period fields apply and the
 * address carries only these three.
 *
 * The URL is checked here the same way the server checks it, so a malformed URL or one outside
 * the property is refused in the bar, with the input left as typed, rather than spending the
 * property's inspection quota on an error Search Console is certain to return. Apart from
 * trimming the ends, the URL is never rewritten: its query string and fragment are part of what
 * Google indexed, and "tidying" them would inspect a different URL.
 */

const DOMAIN_PROPERTY_PREFIX = 'sc-domain:';

/**
 * Languages Google can return its issue messages in, offered as BCP-47 codes. The setting changes
 * the wording of returned messages only, never which evidence is returned.
 */
export const LANGUAGES = ['en-US', 'en-GB', 'de-DE', 'fr-FR', 'es-ES', 'it-IT', 'pt-BR', 'nl-NL'];
export const DEFAULT_LANGUAGE = 'en-US';

/** The query-string keys this report owns. */
export const URL_KEYS = ['site', 'url', 'lang'];

function parseUrl(value) {
    try {
        return new URL(value);
    } catch (err) {
        return null;
    }
}

/** Whether `value` is an absolute http(s) URL with a host. */
export function isFullyQualifiedUrl(value) {
    const url = parseUrl(value);
    return !!url && ['http:', 'https:'].includes(url.protocol) && !!url.hostname;
}

/**
 * Whether `value` belongs to the Search Console property `siteUrl`.
 *
 * A Domain property covers every scheme and subdomain of its domain; a URL-prefix property covers
 * exactly the URLs that start with it — same scheme, host and port, and a path under its path.
 * Hosts compare case-insensitively (the URL parser lower-cases them), paths do not.
 */
export function isUrlInProperty(value, siteUrl) {
    const url = parseUrl(value);
    if (!url || !siteUrl) return false;

    if (siteUrl.startsWith(DOMAIN_PROPERTY_PREFIX)) {
        const domain = siteUrl
            .slice(DOMAIN_PROPERTY_PREFIX.length)
            .trim()
            .toLowerCase()
            .replace(/\.$/, '');
        return !!domain && (url.hostname === domain || url.hostname.endsWith(`.${domain}`));
    }

    const prefix = parseUrl(siteUrl);
    if (!prefix) return false;

    return (
        url.protocol === prefix.protocol &&
        url.hostname === prefix.hostname &&
        url.port === prefix.port &&
        url.pathname.startsWith(prefix.pathname)
    );
}

export function initialDraft() {
    return {
        siteUrl: '',
        permissionLevel: '',
        inspectionUrl: '',
        languageCode: DEFAULT_LANGUAGE,
    };
}

/**
 * The scope the draft describes, with what stops it from being inspected.
 *
 * An empty URL is not an error — it is the state the page opens in — so it holds the Inspect
 * button back without putting a warning on screen; `ready` says whether there is a request to send.
 */
export function resolveReportScope(draft, t) {
    const inspectionUrl = (draft.inspectionUrl ?? '').trim();
    const errors = [];

    if (!draft.siteUrl) {
        errors.push(t('tools.gsc.errors.no_property'));
    } else if (inspectionUrl && !isFullyQualifiedUrl(inspectionUrl)) {
        errors.push(t('tools.url_inspection.errors.invalid_url'));
    } else if (inspectionUrl && !isUrlInProperty(inspectionUrl, draft.siteUrl)) {
        errors.push(t('tools.url_inspection.errors.outside_property', { property: draft.siteUrl }));
    }

    return {
        siteUrl: draft.siteUrl,
        inspectionUrl,
        languageCode: LANGUAGES.includes(draft.languageCode)
            ? draft.languageCode
            : DEFAULT_LANGUAGE,
        errors,
        warnings: [],
        ready: !!inspectionUrl && !errors.length,
    };
}

/**
 * The draft a link describes. A URL arriving this way fills the field and nothing more: inspecting
 * spends the property's quota, so it waits for the user to press Inspect.
 */
export function reportDraftFromParams(params) {
    const draft = {};

    if (params.site) draft.siteUrl = params.site;
    if (params.url) draft.inspectionUrl = params.url;
    if (LANGUAGES.includes(params.lang)) draft.languageCode = params.lang;

    return draft;
}

export function reportParamsFromScope(scope) {
    return { site: scope.siteUrl, url: scope.inspectionUrl, lang: scope.languageCode };
}
